import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { DemoDataApiService } from '@anduin/api';
import {
  AnduinUser,
  AuthService,
  ClientManagementConfigService,
  FirmPortalUserPermission,
  LaunchDarklyFlag,
  LaunchDarklyService,
  PermissionsState,
  ProjectConfigService,
  UserService,
} from '@anduin/auth';
import { ConfigService, Globals, ReportingProvider, SelectReportingProviderUseCase } from '@anduin/config';
import { DEFAULT_SNACKBAR_CONFIG, LoadableHelper, SharedState, SnackbarComponent, SnackbarType, getLoadedResults } from '@anduin/shared';
import { Store } from '@ngxs/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import {
  ROUTE_TOKEN_ACCOUNTS_RECEIVABLE,
  ROUTE_TOKEN_BILLING_BASE,
  ROUTE_TOKEN_CLIENTS_AND_CONTACTS,
  ROUTE_TOKEN_CLIENT_MANAGEMENT,
  ROUTE_TOKEN_COLLECTIONS_BASE,
  ROUTE_TOKEN_DOCUMENTS,
  ROUTE_TOKEN_DRAFT_INVOICES,
  ROUTE_TOKEN_ENGAGEMENTS,
  ROUTE_TOKEN_PAYMENTS,
  ROUTE_TOKEN_PROJECTS_BASE,
  ROUTE_TOKEN_SETTINGS,
  ROUTE_TOKEN_STAFF,
  ROUTE_TOKEN_TIMESHEET,
} from '../../routing-constants';
import { LoginService } from '../../services/login.service';
import { AppState } from '../../state/app.state';
import { ZendeskWidgetService } from '../../services/zendesk-widget.service';
import { SideNavLinkOption } from './sidenav-link-option/sidenav-link-option.component';
import { SideNavMenuOption } from './sidenav-menu-option/sidenav-menu-option.component';
import { SidenavStateService } from './sidenav-state.service';

type SideNavOption = SideNavLinkOption | SideNavMenuOption;

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
})
export class SidenavComponent implements OnInit, OnDestroy {
  selectReportingProvider = inject(SelectReportingProviderUseCase);
  _unsubscribe$ = new Subject<void>();

  ROUTE_COLLECTIONS = `/${ROUTE_TOKEN_COLLECTIONS_BASE}`;
  ROUTE_BILLING = `/${ROUTE_TOKEN_BILLING_BASE}`;
  ROUTE_JOBS = `/${ROUTE_TOKEN_BILLING_BASE}/jobs`;
  ROUTE_ALL_JOBS = `/${ROUTE_TOKEN_PROJECTS_BASE}/all-jobs`;
  ROUTE_PROJECT_DETAIL_PAGE = `/${ROUTE_TOKEN_BILLING_BASE}/job`;
  ROUTE_DRAFT_BILLS = `/${ROUTE_TOKEN_BILLING_BASE}/draft-bills`;
  ROUTE_DRAFT_INVOICES_NEW = `/${ROUTE_TOKEN_DRAFT_INVOICES}`;
  ROUTE_ENGAGEMENTS = `/${ROUTE_TOKEN_ENGAGEMENTS}`;
  ROUTE_DOCUMENTS = `/${ROUTE_TOKEN_DOCUMENTS}`;
  ROUTE_SETTINGS = `/${ROUTE_TOKEN_SETTINGS}`;
  ROUTE_CLIENT_MANAGEMENT = `/${ROUTE_TOKEN_CLIENT_MANAGEMENT}`;
  ROUTE_CLIENTS_AND_CONTACTS = `/${ROUTE_TOKEN_CLIENTS_AND_CONTACTS}`;
  ROUTE_STAFF = `/${ROUTE_TOKEN_STAFF}`;
  ROUTE_TIMESHEET = `/${ROUTE_TOKEN_TIMESHEET}`;
  ROUTE_HOME = `/home`;

  enhancedLayoutEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(
    LaunchDarklyFlag.ENABLE_UPDATED_FIRM_SETTINGS_LAYOUT,
  );
  entityManagementEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_ENTITY_MANAGEMENT);
  myWorkHomePageEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(
    LaunchDarklyFlag.ENABLE_MY_WORK_HOME_PAGE,
  ) as Observable<boolean>;
  shareFileLinksEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_SHARE_FILE_LINKS);

  public sidenavOptions: SideNavOption[] = [];
  public sidenavFooterOptions: SideNavOption[] = [];

  public hideLogoutLink = true;
  public collapsed = false;
  public collapsing = false;
  public menuOpen = false;
  public isHovering = false;
  private readonly _isGeneratingDemoData$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  toggleSidebar() {
    // Prevent firing hover event until collapse animation is complete
    this.setCollapseState(!this.collapsed);
    this.collapsing = true;
    setTimeout(() => {
      this.collapsing = false;
    }, 300);
  }

  hasReportingPermissions$: Observable<boolean> = this.store.select(PermissionsState.getReportingPermissions);

  reportingOnly$: Observable<boolean> = this.store.select(PermissionsState.getStatus).pipe(
    filter((status) => !LoadableHelper.isLoading(status)),
    switchMap(() => this.store.select(PermissionsState.reportingOnly)),
  );

  user$ = this.userService.user$.pipe(
    filter((user) => user !== undefined),
    tap((user: AnduinUser) => {
      if (user) {
        this.loginService.user = user;
        this.hideLogoutLink = false;
      } else {
        this.loginService.user = null;
        this.hideLogoutLink = true;
      }
      this.ref.detectChanges();
    }),
    takeUntil(this._unsubscribe$),
  );

  assignedDraftBillCount$: Observable<number> = this.store.select(AppState.getAssignedDraftBillCount);
  protected version$: Observable<string> = this.store.select(SharedState.getAppVersion);

  userHasStaffId$: Observable<boolean> = this.store.select(PermissionsState.getStatus).pipe(
    filter((status) => !LoadableHelper.isLoading(status)),
    map(() => this.authService.getStaffId() != null),
  );

  copyLogRocketEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_HELP_BUTTON_TO_COPY_LOGROCKET_URL);

  chatbotEnabled$: Observable<boolean> = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_FIRM_PORTAL_CHATBOT);

  constructor(
    public loginService: LoginService,
    private readonly launchDarklyService: LaunchDarklyService,
    private userService: UserService,
    private ref: ChangeDetectorRef,
    private router: Router,
    private store: Store,
    protected configService: ConfigService,
    public clientManagementConfigService: ClientManagementConfigService,
    public projectConfigService: ProjectConfigService,
    public authService: AuthService,
    private demoDataApiService: DemoDataApiService,
    private readonly snackbar: MatSnackBar,
    private readonly globals: Globals,
    private readonly sidenavStateService: SidenavStateService,
    private readonly zendeskWidgetService: ZendeskWidgetService,
  ) {}

  ngOnInit(): void {
    this.collapsed = this.getCollapseState();
    this.sidenavStateService.collapsed.set(this.collapsed);
    this.user$.subscribe();
    combineLatest([
      this.configService.isConfigEnabled('engagementsEnabled'),
      this.configService.isConfigEnabled('billingEnabled'),
      this.configService.isConfigEnabled('collectionsEnabled'),
      this.configService.isConfigEnabled('clientManagementEnabled'),
      this.configService.isConfigEnabled('timesheetEnabled'),
      this.configService.isConfigEnabled('generateDemoDataEnabled'),
      this.configService.isConfigEnabled('practiceBillingEnabled'),
      this.configService.isConfigEnabled('projectManagementEnabled'),
      this.hasReportingPermissions$,
      this.userHasStaffId$,
      getLoadedResults(this.store.select(PermissionsState.getState)),
      this.reportingOnly$,
      this.entityManagementEnabled$,
      this.clientManagementConfigService.contactManagementEnabled$,
      this.myWorkHomePageEnabled$,
      this.projectConfigService.allProjectsEnabled$,
      this.projectConfigService.clientExperiencePortalEnabled$,
      this.copyLogRocketEnabled$,
      this.shareFileLinksEnabled$,
      this.configService.configsLoaded$.pipe(map((_) => this.selectReportingProvider.execute() === ReportingProvider.THOUGHT_SPOT)),
      this.chatbotEnabled$,
    ])
      .pipe(
        takeUntil(this._unsubscribe$),
        tap(
          ([
            engagementsEnabled,
            billingEnabled,
            collectionsEnabled,
            clientManagementEnabled,
            timesheetEnabled,
            generateDemoDataEnabled,
            practiceBillingEnabled,
            projectManagementEnabled,
            hasReportingPermissions,
            userHasStaffId,
            userPermissions,
            reportingOnly,
            entityManagementEnabled,
            contactManagementEnabled,
            myWorkHomePageEnabled,
            allProjectsEnabled,
            clientExperiencePortalEnabled,
            copyLogRocketEnabled,
            shareFileLinksEnabled,
            thoughtSpotEnabled,
            chatbotEnabled,
          ]) => {
            this.sidenavOptions = this.getSidenavOptions({
              engagementsEnabled,
              billingEnabled,
              collectionsEnabled,
              clientManagementEnabled,
              timesheetEnabled,
              hasReportingPermissions,
              userPermissions,
              reportingOnly,
              entityManagementEnabled,
              contactManagementEnabled,
              myWorkHomePageEnabled,
              allProjectsEnabled,
              clientExperiencePortalEnabled,
              practiceBillingEnabled,
              shareFileLinksEnabled,
              thoughtSpotEnabled,
            });
            this.sidenavFooterOptions = this.getSidebarFooterOptions({
              hasStaffId: userHasStaffId,
              userPermissions,
              generateDemoDataEnabled,
              engagementsEnabled,
              billingEnabled,
              collectionsEnabled,
              practiceBillingEnabled,
              projectManagementEnabled,
              copyLogRocketEnabled,
              chatbotEnabled,
            });
          },
        ),
      )
      .subscribe();
  }

  private isSettingsEnabled(
    userPermissions: FirmPortalUserPermission,
    engagementsEnabled: boolean,
    billingEnabled: boolean,
    collectionsEnabled: boolean,
    projectManagementEnabled: boolean,
  ): boolean {
    const {
      collections_can_edit_collections_settings,
      engagements_manage_engagement_letter_section_templates,
      engagements_manage_engagement_letter_templates,
      engagements_manage_job_templates,
      engagements_can_edit_engagements_settings,
      billing_edit_auto_generated_settings,
      client_collaboration_can_view_request_list,
      time_management_view_settings,
    } = userPermissions;

    // Check if user has any settings permissions
    const hasSettingsPermissions =
      collections_can_edit_collections_settings ||
      engagements_manage_engagement_letter_section_templates ||
      engagements_manage_engagement_letter_templates ||
      engagements_manage_job_templates ||
      engagements_can_edit_engagements_settings ||
      billing_edit_auto_generated_settings ||
      client_collaboration_can_view_request_list ||
      time_management_view_settings;

    // Check if at least one required config is enabled (matches route guard logic)
    const hasRequiredConfig = engagementsEnabled || billingEnabled || collectionsEnabled || projectManagementEnabled;

    return hasRequiredConfig && hasSettingsPermissions;
  }

  getSidebarFooterOptions({
    hasStaffId,
    userPermissions,
    generateDemoDataEnabled,
    engagementsEnabled,
    billingEnabled,
    collectionsEnabled,
    practiceBillingEnabled,
    projectManagementEnabled,
    copyLogRocketEnabled,
    chatbotEnabled,
  }: {
    hasStaffId: boolean;
    userPermissions: FirmPortalUserPermission;
    generateDemoDataEnabled: boolean;
    engagementsEnabled: boolean;
    billingEnabled: boolean;
    collectionsEnabled: boolean;
    practiceBillingEnabled: boolean;
    projectManagementEnabled: boolean;
    copyLogRocketEnabled: boolean;
    chatbotEnabled: boolean;
  }): SideNavOption[] {
    return [
      {
        enabled: this.isSettingsEnabled(userPermissions, engagementsEnabled, billingEnabled, collectionsEnabled, projectManagementEnabled),
        type: 'link',
        label: 'Settings',
        icon: 'settings',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: this.ROUTE_SETTINGS }],
      },
      {
        enabled: true,
        type: 'menu',
        label: 'Help',
        icon: 'life-buoy',
        menu: [
          {
            enabled: true,
            type: 'link',
            label: 'Firm Help Center',
            icon: 'info',
            iconNamespace: 'feather-icon',
            link: 'https://www.firmsupport.aiwyn.ai/hc/en-us',
            target: '_blank',
          },
          {
            enabled: true,
            type: 'link',
            label: 'Client Help Center',
            icon: 'info',
            iconNamespace: 'feather-icon',
            link: 'https://www.clientsupport.aiwyn.ai/hc/en-us',
            target: '_blank',
          },
          {
            enabled: true,
            type: 'link',
            label: 'Contact Support',
            icon: 'help-circle',
            iconNamespace: 'feather-icon',
            link: `https://www.firmsupport.aiwyn.ai/hc/en-us/requests/new`,
            target: '_blank',
          },
          {
            enabled: chatbotEnabled,
            type: 'action',
            label: 'Get Instant Support',
            icon: 'message-circle',
            iconNamespace: 'feather-icon',
            action: () => this.zendeskWidgetService.openMessenger(),
          },
          {
            enabled: copyLogRocketEnabled,
            type: 'action',
            label: 'Copy Logrocket Session',
            icon: 'copy',
            iconNamespace: 'feather-icon',
            action: () => this.copyLogRocketSession(),
          },
        ],
        onMenuOpen: () => {
          this.preventMouseEvents();
          this.menuOpen = true;
        },
        onMenuClose: () => {
          this.menuOpen = false;
        },
      },
      {
        enabled: hasStaffId,
        type: 'menu',
        label: this.getUserName(),
        icon: 'user',
        menu: [
          {
            enabled: generateDemoDataEnabled,
            type: 'action',
            label: 'Generate Demo Data',
            icon: 'award',
            action: () => this.generateDemoData(),
          },
          {
            enabled: true,
            type: 'action',
            label: 'Logout',
            icon: 'log-out',
            action: () => this.logout(),
          },
        ],
        onMenuOpen: () => {
          this.preventMouseEvents();
          this.menuOpen = true;
        },
        onMenuClose: () => {
          this.menuOpen = false;
        },
      },
    ];
  }

  getSidenavOptions({
    engagementsEnabled,
    billingEnabled,
    collectionsEnabled,
    clientManagementEnabled,
    timesheetEnabled,
    hasReportingPermissions,
    userPermissions,
    reportingOnly,
    entityManagementEnabled,
    contactManagementEnabled,
    myWorkHomePageEnabled,
    allProjectsEnabled,
    clientExperiencePortalEnabled,
    practiceBillingEnabled,
    shareFileLinksEnabled,
    thoughtSpotEnabled,
  }: {
    engagementsEnabled: boolean;
    billingEnabled: boolean;
    collectionsEnabled: boolean;
    clientManagementEnabled: boolean;
    timesheetEnabled: boolean;
    hasReportingPermissions: boolean;
    userPermissions: FirmPortalUserPermission;
    reportingOnly: boolean;
    entityManagementEnabled: boolean;
    contactManagementEnabled: boolean;
    myWorkHomePageEnabled: boolean;
    allProjectsEnabled: boolean;
    clientExperiencePortalEnabled: boolean;
    practiceBillingEnabled: boolean;
    shareFileLinksEnabled: boolean;
    thoughtSpotEnabled: boolean;
  }): SideNavOption[] {
    const {
      can_view_engagements,
      can_view_billing,
      can_view_payments,
      collections_can_view_collections,
      payments_can_view_invoices,
      payments_can_view_payments,
      payments_can_view_clients,
      documents_view_documents,
    } = userPermissions;

    return [
      {
        enabled: myWorkHomePageEnabled,
        type: 'link',
        label: 'Home',
        icon: 'home',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: false },
        routes: [{ path: this.ROUTE_HOME }],
      },
      {
        enabled:
          clientManagementEnabled &&
          !entityManagementEnabled &&
          !contactManagementEnabled &&
          can_view_payments &&
          payments_can_view_clients &&
          !reportingOnly,
        type: 'link',
        label: 'Clients',
        icon: 'users',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: false },
        routes: [{ path: this.ROUTE_CLIENT_MANAGEMENT }],
      },
      {
        enabled:
          clientManagementEnabled &&
          (entityManagementEnabled || contactManagementEnabled) &&
          can_view_payments &&
          payments_can_view_clients &&
          !reportingOnly,
        type: 'link',
        label: 'Clients & Contacts',
        icon: 'users',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: false },
        routes: [{ path: this.ROUTE_CLIENTS_AND_CONTACTS }],
      },
      {
        enabled: !clientManagementEnabled && can_view_payments && payments_can_view_clients && !reportingOnly,
        type: 'link',
        label: 'Clients',
        icon: 'users',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: true },
        routes: [{ path: '/clients' }],
      },
      {
        enabled: timesheetEnabled && !reportingOnly,
        type: 'link',
        label: 'Timesheet',
        icon: 'calendar',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: false },
        routes: [{ path: this.ROUTE_TIMESHEET }],
      },
      {
        enabled: engagementsEnabled && can_view_engagements && !reportingOnly,
        type: 'link',
        label: 'Engagements',
        icon: 'file-text',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: this.ROUTE_ENGAGEMENTS }],
      },
      {
        enabled: shareFileLinksEnabled && !!documents_view_documents,
        type: 'link',
        label: 'Documents',
        icon: 'file',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: this.ROUTE_DOCUMENTS }],
      },
      {
        enabled: allProjectsEnabled || (clientExperiencePortalEnabled && userPermissions.client_collaboration_can_view_request_list),
        type: 'link',
        label: 'Projects',
        icon: 'check-circle',
        onSelect: () => this.preventMouseEvents(),
        routes: [
          { path: this.ROUTE_ALL_JOBS },
          { path: this.ROUTE_PROJECT_DETAIL_PAGE },
          { path: `/${ROUTE_TOKEN_PROJECTS_BASE}/planning` },
        ],
      },
      {
        enabled: billingEnabled && can_view_billing && !reportingOnly,
        type: 'link',
        label: practiceBillingEnabled ? 'WIP' : 'Projects',
        icon: practiceBillingEnabled ? 'unbilled-wip' : 'check-circle',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: this.ROUTE_JOBS }],
      },
      {
        enabled: billingEnabled && can_view_billing && !reportingOnly,
        type: 'link',
        label: 'Draft Invoices',
        icon: 'upload',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: practiceBillingEnabled ? this.ROUTE_DRAFT_INVOICES_NEW : this.ROUTE_DRAFT_BILLS }],
      },
      {
        enabled: can_view_payments && payments_can_view_invoices && !reportingOnly,
        type: 'link',
        label: 'Invoices',
        icon: 'invoices',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: '/invoices' }],
      },
      {
        enabled: can_view_payments && payments_can_view_payments && !reportingOnly,
        type: 'link',
        label: practiceBillingEnabled ? 'Accounts Receivable' : 'Payments',
        icon: 'dollar-sign',
        onSelect: () => this.preventMouseEvents(),
        routerLinkActiveOptions: { exact: false },
        routes: [{ path: `/${practiceBillingEnabled ? ROUTE_TOKEN_ACCOUNTS_RECEIVABLE : ROUTE_TOKEN_PAYMENTS}` }],
      },
      {
        enabled: collectionsEnabled && collections_can_view_collections && !reportingOnly,
        type: 'link',
        label: 'Collections',
        icon: 'send',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: this.ROUTE_COLLECTIONS }],
      },
      {
        enabled: hasReportingPermissions && thoughtSpotEnabled,
        type: 'link',
        label: 'Reports',
        icon: 'bar-chart',
        onSelect: () => this.preventMouseEvents(),
        routes: [{ path: '/reports' }],
      },
    ];
  }

  preventMouseEvents = (time = 300) => {
    this.collapsing = true;
    setTimeout(() => {
      this.collapsing = false;
    }, time);
  };

  setCollapseState(collapsed: boolean) {
    this.collapsed = collapsed;
    localStorage.setItem('sidenavCollapsed', this.collapsed.toString());
    this.sidenavStateService.collapsed.set(collapsed);
  }

  getCollapseState(): boolean {
    const collapsed = localStorage.getItem('sidenavCollapsed');
    return collapsed === 'true';
  }

  logout(): void {
    this.hideLogoutLink = true;
    this.router.navigateByUrl('logout');
  }

  generateDemoData(): void {
    if (this._isGeneratingDemoData$.value) {
      return;
    }

    this._isGeneratingDemoData$.next(true);
    this.snackbar.openFromComponent(SnackbarComponent, {
      ...DEFAULT_SNACKBAR_CONFIG,
      data: {
        message: `Starting to generate data, this may take a moment.`,
        type: SnackbarType.INFO,
      },
    });
    this.demoDataApiService
      .generateDemoData()
      .pipe(
        tap(
          () => {
            this.snackbar.openFromComponent(SnackbarComponent, {
              ...DEFAULT_SNACKBAR_CONFIG,
              data: {
                message: `Data has been generated.`,
                type: SnackbarType.SUCCESS,
              },
            });
          },
          () =>
            this.snackbar.openFromComponent(SnackbarComponent, {
              ...DEFAULT_SNACKBAR_CONFIG,
              data: {
                message: `Failed to generate data. Please attempt your request again later.`,
                type: SnackbarType.ERROR,
              },
            }),
        ),
        take(1),
        finalize(() => this._isGeneratingDemoData$.next(false)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  getUserName(): string {
    return this.loginService.user?.displayName;
  }

  onSidenavMouseEnter(): void {
    if (this.collapsed && !this.collapsing) {
      this.isHovering = true;
    }
  }

  onSidenavMouseLeave(): void {
    this.isHovering = false;
  }

  copyLogRocketSession(): void {
    const sessionUrl = this.globals.logRocketSessionUrl;

    if (!sessionUrl) {
      navigator.clipboard.writeText('No LogRocket session URL available');
      // Show error toast if no session URL is available
      this.snackbar.openFromComponent(SnackbarComponent, {
        ...DEFAULT_SNACKBAR_CONFIG,
        data: {
          message: 'No LogRocket session URL available to copy.',
          type: SnackbarType.ERROR,
        },
      });
      return;
    }

    // Copy the actual LogRocket session URL to clipboard
    navigator.clipboard.writeText(sessionUrl).then(
      () => {
        // Show success toast with the actual URL
        this.snackbar.openFromComponent(SnackbarComponent, {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: `LogRocket session URL copied: ${sessionUrl}`,
            type: SnackbarType.SUCCESS,
          },
        });
      },
      () => {
        // Show error toast if clipboard copy fails
        this.snackbar.openFromComponent(SnackbarComponent, {
          ...DEFAULT_SNACKBAR_CONFIG,
          data: {
            message: 'Failed to copy LogRocket session URL to clipboard.',
            type: SnackbarType.ERROR,
          },
        });
      },
    );
  }
}
